import type { EnvironmentId, PullRequestRef, PullRequestReviewThread } from "@cmmd-forge/contracts";
import { useState } from "react";

import { cmmdApiClient } from "~/environments/cmmdApi";
import { commandPullRequestRef } from "~/lib/pullRequestReactQuery";
import {
  appendUniquePullRequestThreadComments,
  pullRequestThreadSnapshotKey,
} from "./pullRequestThreadPagination.logic";

type ThreadComment = PullRequestReviewThread["comments"][number];
type ThreadState = {
  readonly snapshotKey: string;
  readonly loaded: ReadonlyArray<ThreadComment>;
  readonly cursor: string | null;
};

export function usePullRequestThreadPagination(
  _environmentId: EnvironmentId,
  reference: PullRequestRef,
  thread: PullRequestReviewThread,
) {
  const snapshotKey = pullRequestThreadSnapshotKey(thread);
  const initialState = createInitialThreadState(snapshotKey, thread);
  const [threadState, setThreadState] = useState(initialState);
  const activeState = threadState.snapshotKey === snapshotKey ? threadState : initialState;
  if (threadState.snapshotKey !== snapshotKey) setThreadState(initialState);

  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const loadMore = () => {
    if (activeState.cursor === null || loadingMore) return;
    setLoadingMore(true);
    setLoadError(null);
    void loadOlderReplies(reference, thread.id, activeState.cursor)
      .then((page) => appendLoadedPage(setThreadState, snapshotKey, thread.comments, page))
      .catch((error: unknown) => setLoadError(toLoadError(error)))
      .finally(() => setLoadingMore(false));
  };

  return {
    comments: [...thread.comments, ...activeState.loaded],
    hasMore: activeState.cursor !== null,
    loadError,
    loadingMore,
    loadMore,
  };
}

function createInitialThreadState(
  snapshotKey: string,
  thread: PullRequestReviewThread,
): ThreadState {
  return { snapshotKey, loaded: [], cursor: thread.nextCommentsCursor ?? null };
}

async function loadOlderReplies(reference: PullRequestRef, threadId: string, cursor: string) {
  return cmmdApiClient.getPullRequestThreadComments({
    ...commandPullRequestRef(reference),
    threadId,
    cursor,
  });
}

function appendLoadedPage(
  setThreadState: (state: (previous: ThreadState) => ThreadState) => void,
  snapshotKey: string,
  initial: PullRequestReviewThread["comments"],
  page: {
    readonly comments: ReadonlyArray<ThreadComment>;
    readonly nextCursor: string | null;
  },
) {
  setThreadState((previous) => {
    if (previous.snapshotKey !== snapshotKey) return previous;
    return {
      ...previous,
      loaded: appendUniquePullRequestThreadComments(initial, previous.loaded, page.comments),
      cursor: page.nextCursor,
    };
  });
}

function toLoadError(error: unknown): Error {
  return error instanceof Error ? error : new Error("Older replies could not be loaded.");
}
